/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRemoteSessionClientsRequest,
  ListRemoteSessionClientsSecurity,
} from "../models/operations/listremotesessionclients.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRemoteSessionClientsInfiniteQuery,
  buildRemoteSessionClientsQuery,
  prefetchRemoteSessionClients,
  prefetchRemoteSessionClientsInfinite,
  queryKeyRemoteSessionClients,
  queryKeyRemoteSessionClientsInfinite,
  RemoteSessionClientsInfiniteQueryData,
  RemoteSessionClientsPageParams,
  RemoteSessionClientsQueryData,
} from "./remoteSessionClients.core.js";
export {
  buildRemoteSessionClientsInfiniteQuery,
  buildRemoteSessionClientsQuery,
  prefetchRemoteSessionClients,
  prefetchRemoteSessionClientsInfinite,
  queryKeyRemoteSessionClients,
  queryKeyRemoteSessionClientsInfinite,
  type RemoteSessionClientsInfiniteQueryData,
  type RemoteSessionClientsPageParams,
  type RemoteSessionClientsQueryData,
};

export type RemoteSessionClientsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRemoteSessionClients remoteSessionClients
 *
 * @remarks
 * List remote_session_clients in the caller's project.
 */
export function useRemoteSessionClients(
  request?: ListRemoteSessionClientsRequest | undefined,
  security?: ListRemoteSessionClientsSecurity | undefined,
  options?: QueryHookOptions<
    RemoteSessionClientsQueryData,
    RemoteSessionClientsQueryError
  >,
): UseQueryResult<
  RemoteSessionClientsQueryData,
  RemoteSessionClientsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildRemoteSessionClientsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRemoteSessionClients remoteSessionClients
 *
 * @remarks
 * List remote_session_clients in the caller's project.
 */
export function useRemoteSessionClientsSuspense(
  request?: ListRemoteSessionClientsRequest | undefined,
  security?: ListRemoteSessionClientsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RemoteSessionClientsQueryData,
    RemoteSessionClientsQueryError
  >,
): UseSuspenseQueryResult<
  RemoteSessionClientsQueryData,
  RemoteSessionClientsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRemoteSessionClientsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRemoteSessionClients remoteSessionClients
 *
 * @remarks
 * List remote_session_clients in the caller's project.
 */
export function useRemoteSessionClientsInfinite(
  request?: ListRemoteSessionClientsRequest | undefined,
  security?: ListRemoteSessionClientsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    RemoteSessionClientsInfiniteQueryData,
    RemoteSessionClientsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    RemoteSessionClientsInfiniteQueryData,
    RemoteSessionClientsPageParams
  >,
  RemoteSessionClientsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    RemoteSessionClientsInfiniteQueryData,
    RemoteSessionClientsQueryError,
    InfiniteData<
      RemoteSessionClientsInfiniteQueryData,
      RemoteSessionClientsPageParams
    >,
    QueryKey,
    RemoteSessionClientsPageParams
  >({
    ...buildRemoteSessionClientsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listRemoteSessionClients remoteSessionClients
 *
 * @remarks
 * List remote_session_clients in the caller's project.
 */
export function useRemoteSessionClientsInfiniteSuspense(
  request?: ListRemoteSessionClientsRequest | undefined,
  security?: ListRemoteSessionClientsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    RemoteSessionClientsInfiniteQueryData,
    RemoteSessionClientsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    RemoteSessionClientsInfiniteQueryData,
    RemoteSessionClientsPageParams
  >,
  RemoteSessionClientsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    RemoteSessionClientsInfiniteQueryData,
    RemoteSessionClientsQueryError,
    InfiniteData<
      RemoteSessionClientsInfiniteQueryData,
      RemoteSessionClientsPageParams
    >,
    QueryKey,
    RemoteSessionClientsPageParams
  >({
    ...buildRemoteSessionClientsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setRemoteSessionClientsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      remoteSessionIssuerId?: string | undefined;
      userSessionIssuerId?: string | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RemoteSessionClientsQueryData,
): RemoteSessionClientsQueryData | undefined {
  const key = queryKeyRemoteSessionClients(...queryKeyBase);

  return client.setQueryData<RemoteSessionClientsQueryData>(key, data);
}

export function invalidateRemoteSessionClients(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      remoteSessionIssuerId?: string | undefined;
      userSessionIssuerId?: string | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteSessionClients", "list", ...queryKeyBase],
  });
}

export function invalidateAllRemoteSessionClients(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteSessionClients", "list"],
  });
}
